<template>
  <div>
    <v-dialog persistent v-model="internalDialog" max-width="1200">
      <v-card class="bus-change-dialog">
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-bus-clock</v-icon>
          <span class="dialog-title">{{ dialogTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-4">
          <!-- Selected Student Card -->
          <div v-if="busChange">
            <v-card class="selected-student-card mb-4" outlined>
              <v-card-text class="pa-0">
                <v-layout align-center>
                  <v-avatar color="rgb(92, 184, 96)" size="40" class="mr-2">
                    <v-icon dark size="24">mdi-account</v-icon>
                  </v-avatar>
                  <v-flex>
                    <v-layout row wrap>
                      <v-flex xs12 sm6 class="pr-2">
                        <div class="body-2 font-weight-medium pb-0">
                          {{ busChange.studentName }}
                          <span
                            class="caption grey--text ml-1"
                            v-if="busChange.studentNameHeb"
                          >
                            ({{ busChange.studentNameHeb }})
                          </span>
                        </div>
                        <div class="caption grey--text">
                          <strong>Student ID:</strong>
                          {{ busChange.studentID }} |
                          <strong>Building:</strong>
                          {{ busChange.building }} | <strong>Class:</strong>
                          {{ busChange.class }}-{{ busChange.section }}
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <div
                          class="caption grey--text"
                          v-if="busChange.teacherH || busChange.teacherH2"
                        >
                          <v-icon x-small class="mr-1">mdi-account-tie</v-icon>
                          {{ getTeacherDisplay() }}
                        </div>
                        <div
                          class="caption grey--text"
                          v-if="busChange.homeAddress"
                        >
                          <v-icon x-small class="mr-1">mdi-home</v-icon>
                          {{ busChange.homeAddress }}
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-card>
          </div>

          <!-- Record Info with Recording -->
          <div v-if="busChange" class="mb-3 record-info-with-audio">
            <div class="record-info-compact">
              <span class="info-item">
                <v-icon x-small class="mr-1">mdi-source-branch</v-icon>
                {{ busChange.source || "-" }}
              </span>
              <span class="info-divider">•</span>
              <span class="info-item">
                <v-icon x-small class="mr-1">mdi-account-edit</v-icon>
                {{ busChange.enteredBy || "-" }}
              </span>
              <span class="info-divider">•</span>
              <span class="info-item">
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                {{ formatDateTime(busChange.enteredOn) }}
              </span>
            </div>

            <!-- Recording Section (if recording exists) -->
            <div v-if="busChange.recordingURL" class="recording-inline">
              <div class="recording-header">
                <v-icon small color="#ff6b6b" class="recording-icon"
                  >mdi-microphone</v-icon
                >
                <span class="recording-label">Original Request</span>
              </div>
              <audio
                ref="audioPlayer"
                controls
                controlsList="nodownload"
                :src="busChange.recordingURL"
                class="recording-player"
              ></audio>
            </div>
          </div>

          <v-layout v-if="busChange" row wrap>
            <v-flex xs12 md3 class="pr-2">
              <div class="section-header mb-3">
                <v-icon small class="mr-1">mdi-account-details</v-icon>
                Info
              </div>
              <v-card outlined class="pa-2 compact-info">
                <div class="info-row">
                  <span class="info-label">Student:</span>
                  <span class="info-value">{{ busChange.studentName || "-" }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ID:</span>
                  <span class="info-value">{{ busChange.studentID || "-" }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Entered:</span>
                  <span class="info-value">{{
                    formatDateTime(busChange.enteredOn)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">By:</span>
                  <span class="info-value">{{ busChange.enteredBy || "-" }}</span>
                </div>
              </v-card>
            </v-flex>

            <!-- Bus Change Details Section -->
            <v-flex xs12 md6 class="px-2">
            <div class="section-header mb-3">
              <v-icon small class="mr-1">mdi-bus-marker</v-icon>
              Bus Change Details
            </div>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-card outlined class="pa-2 period-section-card mb-2">
                  <div class="caption period-title mb-1">Starting Run</div>
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="formData.startDate"
                        label="Start Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                        :disabled="isViewOnly"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.startDate"
                      color="rgb(92, 184, 96)"
                      @input="startDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-select
                    v-model="formData.startPeriod"
                    :items="periodOptions"
                    item-text="label"
                    item-value="value"
                    label="Run *"
                    prepend-inner-icon="mdi-weather-sunset-up"
                    outlined
                    dense
                    hide-details
                    :disabled="isViewOnly"
                  ></v-select>
                </v-card>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-card outlined class="pa-2 period-section-card mb-2">
                  <div class="caption period-title mb-1">Ending After</div>
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="formData.endDate"
                        label="End Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                        :disabled="isViewOnly"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.endDate"
                      color="rgb(92, 184, 96)"
                      :min="formData.startDate"
                      @input="endDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-select
                    v-model="formData.endPeriod"
                    :items="periodOptions"
                    item-text="label"
                    item-value="value"
                    label="Run *"
                    prepend-inner-icon="mdi-weather-sunset-down"
                    outlined
                    dense
                    hide-details
                    :disabled="isViewOnly"
                  ></v-select>
                </v-card>
              </v-flex>
            </v-layout>
            <div
              class="caption grey--text text--darken-1 mb-3 period-helper-text"
            >
              Morning Pickup: Includes the trip TO school. Afternoon Dropoff:
              Includes the trip FROM school.
            </div>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="formData.busChangeAddress"
                  label="Bus Change Address"
                  prepend-inner-icon="mdi-map-marker"
                  outlined
                  dense
                  :readonly="isViewOnly"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-text-field
                  v-model="formData.busChangePhoneNumber"
                  label="Bus Change Phone Number"
                  prepend-inner-icon="mdi-phone"
                  outlined
                  dense
                  :readonly="isViewOnly"
                  mask="###-###-####"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-autocomplete
                  v-model="selectedPickupRoute"
                  :items="filteredPickupRoutes"
                  :filter="customRouteFilter"
                  item-text="description"
                  item-value="id"
                  label="Pickup Route"
                  prepend-inner-icon="mdi-bus-clock"
                  outlined
                  dense
                  clearable
                  :loading="loadingBusRoutes"
                  :disabled="isViewOnly"
                  return-object
                  @change="onPickupRouteChange"
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:selection="{ item }">
                    <div class="route-selection-wrapper">
                      <div class="route-selection-main">
                        {{ item.description }}
                        <span
                          v-if="item.busNumber"
                          class="route-detail-chip"
                          :style="getRouteChipStyle(item)"
                        >
                          Bus #{{ item.busNumber }}
                        </span>
                      </div>
                      <div v-if="item.driver" class="route-selection-driver">
                        <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                      </div>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-tile-content
                      class="route-list-item"
                      :style="
                        getRouteColor(item.color)
                          ? `border-left: 4px solid ${getRouteColor(
                              item.color
                            )}`
                          : ''
                      "
                    >
                      <v-list-tile-title>
                        <strong>{{ item.description }}</strong>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        <span v-if="item.busNumber" class="mr-2">
                          <v-icon x-small>mdi-bus</v-icon> Bus #{{
                            item.busNumber
                          }}
                        </span>
                        <span v-if="item.driver" class="mr-2">
                          <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                        </span>
                        <span v-if="item.color" class="mr-2">
                          <v-icon
                            x-small
                            :style="
                              getRouteColor(item.color)
                                ? `color: ${getRouteColor(item.color)}`
                                : ''
                            "
                            >mdi-circle</v-icon
                          >
                          {{ item.color }}
                        </span>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-autocomplete
                  v-model="selectedDropoffRoute"
                  :items="filteredDropoffRoutes"
                  :filter="customRouteFilter"
                  item-text="description"
                  item-value="id"
                  label="Dropoff Route"
                  prepend-inner-icon="mdi-bus-marker"
                  outlined
                  dense
                  clearable
                  :loading="loadingBusRoutes"
                  :disabled="isViewOnly"
                  return-object
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:selection="{ item }">
                    <div class="route-selection-wrapper">
                      <div class="route-selection-main">
                        {{ item.description }}
                        <span
                          v-if="item.busNumber"
                          class="route-detail-chip"
                          :style="getRouteChipStyle(item)"
                        >
                          Bus #{{ item.busNumber }}
                        </span>
                      </div>
                      <div v-if="item.driver" class="route-selection-driver">
                        <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                      </div>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-tile-content
                      class="route-list-item"
                      :style="
                        getRouteColor(item.color)
                          ? `border-left: 4px solid ${getRouteColor(
                              item.color
                            )}`
                          : ''
                      "
                    >
                      <v-list-tile-title>
                        <strong>{{ item.description }}</strong>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        <span v-if="item.busNumber" class="mr-2">
                          <v-icon x-small>mdi-bus</v-icon> Bus #{{
                            item.busNumber
                          }}
                        </span>
                        <span v-if="item.driver" class="mr-2">
                          <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                        </span>
                        <span v-if="item.color" class="mr-2">
                          <v-icon
                            x-small
                            :style="
                              getRouteColor(item.color)
                                ? `color: ${getRouteColor(item.color)}`
                                : ''
                            "
                            >mdi-palette</v-icon
                          >
                          {{ item.color }}
                        </span>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="formData.busChangeName"
                  label="Bus Change Family Name"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  :readonly="isViewOnly"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-text-field
                  v-model="formData.busChangeAccountID"
                  label="Bus Change Account ID"
                  prepend-inner-icon="mdi-account-search"
                  outlined
                  dense
                  readonly
                  @click="openAccountSearch"
                  style="cursor: pointer;"
                  autocomplete="no-password"
                  :disabled="isViewOnly"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="formData.notificationPhoneNumber"
                  label="Notification Phone"
                  prepend-inner-icon="mdi-bell"
                  outlined
                  dense
                  :readonly="isViewOnly"
                  mask="###-###-####"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-select
                  v-model="formData.notificationType"
                  :items="['MESSAGE', 'CALL']"
                  label="Notification Type"
                  prepend-inner-icon="mdi-message-alert"
                  outlined
                  dense
                  clearable
                  :disabled="isViewOnly"
                ></v-select>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="formData.geoPhoneNumber"
                  label="Geo Tracking Phone Number"
                  prepend-inner-icon="mdi-phone-in-talk"
                  outlined
                  dense
                  :readonly="isViewOnly"
                  mask="###-###-####"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-textarea
                  v-model="formData.note"
                  label="Note"
                  prepend-inner-icon="mdi-note-text"
                  outlined
                  dense
                  rows="2"
                  :readonly="isViewOnly"
                  autocomplete="no-password"
                  hide-details
                ></v-textarea>
              </v-flex>
            </v-layout>
            </v-flex>

            <v-flex xs12 md3 class="pl-2">
              <div class="section-header mb-3">
                <v-icon small class="mr-1">mdi-message-text</v-icon>
                Messages
              </div>
              <v-card outlined class="pa-2 comments-card">
                <div
                  v-if="loadingBusChangeComments"
                  class="d-flex align-center grey--text text--darken-1"
                >
                  <v-progress-circular
                    size="18"
                    width="2"
                    indeterminate
                    color="success"
                    class="mr-2"
                  ></v-progress-circular>
                  Loading...
                </div>
                <div
                  v-else-if="busChangeComments.length === 0"
                  class="grey--text text--darken-1"
                >
                  No messages.
                </div>
                <div v-else>
                  <div
                    v-for="(comment, index) in busChangeComments"
                    :key="comment.id || comment.ID || comment.callUUID || index"
                    class="comment-entry"
                  >
                    <div class="comment-meta">
                      <span class="font-weight-medium">
                        {{ getCommentEnteredBy(comment) || "Parent" }}
                      </span>
                      <span class="mx-1">-</span>
                      <span>{{
                        formatDateTime(getCommentEnteredOn(comment))
                      }}</span>
                    </div>
                    <div
                      v-if="getCommentCallerID(comment)"
                      class="comment-caller grey--text text--darken-1"
                    >
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ formatPhoneNumber(getCommentCallerID(comment)) }}
                    </div>
                    <div
                      v-if="getCommentText(comment)"
                      class="comment-text grey--text text--darken-3"
                    >
                      {{ getCommentText(comment) }}
                    </div>
                    <audio
                      v-if="getCommentRecordingUrl(comment)"
                      controls
                      controlsList="nodownload"
                      :src="getCommentRecordingUrl(comment)"
                      class="comment-audio mt-2"
                    ></audio>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider class="py-0"></v-divider>

        <v-card-actions class="py-3 pr-3">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="close">
            Close
          </v-btn>
          <v-btn
            v-if="!isViewOnly"
            color="success"
            dark
            :loading="saving"
            @click="save"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Confirmation Dialog -->
    <v-dialog v-model="notificationConfirmDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-information</v-icon>
          Notification Will Be Sent
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert value="true" type="info" outline class="mb-3">
            <div class="font-weight-bold mb-1">
              <v-icon small color="info" class="mr-1">
                {{
                  formData.notificationType === "CALL"
                    ? "mdi-phone"
                    : "mdi-message-text"
                }}
              </v-icon>
              {{
                formData.notificationType === "CALL"
                  ? "A phone call"
                  : "A text message"
              }}
              will automatically be sent to:
            </div>
            <div class="ml-4">
              {{ formatPhoneNumber(formData.notificationPhoneNumber) }}
            </div>
          </v-alert>
          <div class="grey--text text--darken-1">
            The bus change will be saved and the notification will be sent
            automatically.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="success" dark @click="proceedWithNotification">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Notification Fields Dialog -->
    <v-dialog
      v-model="missingNotificationFieldsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Missing Notification Information
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outlined class="mb-3">
            <div class="font-weight-bold mb-2">
              Cannot send notification - missing required fields:
            </div>
            <ul class="ml-4">
              <li v-if="!formData.notificationPhoneNumber">
                Notification Phone Number
              </li>
              <li v-if="!formData.notificationType">Notification Type</li>
            </ul>
          </v-alert>
          <div class="mt-3">
            Would you like to fill in these fields or save without sending a
            notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="warning" @click="saveWithoutNotification">
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="closeMissingFieldsDialog">
            Fill in Fields
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cleared Notification Warning Dialog (IVR) -->
    <v-dialog
      v-model="clearedNotificationWarningDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Notification Fields Cleared
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" outlined class="mb-3">
            <div class="font-weight-bold mb-2 grey--text text--darken-4">
              This record originally had notification information
            </div>
            <div class="grey--text text--darken-3">
              By clearing the notification fields, the user will NOT receive a
              notification about this bus change.
            </div>
          </v-alert>
          <div class="mt-3">
            Do you want to continue saving without notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelClearedNotificationWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="800">
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text"
            icon
            dark
            @click="closeAccountSearchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Phone Number Search -->
          <v-layout row wrap class="mb-1">
            <v-flex xs12 class="pr-2">
              <v-text-field
                v-model="dialogPhoneSearch"
                label="Search by Phone Number"
                prepend-inner-icon="mdi-phone"
                outlined
                dense
                clearable
                mask="###-###-####"
                @input="searchDialogByPhone"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Search Name Mode Toggle -->
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 class="pr-2">
              <div class="caption grey--text mb-1">Or Search By Name</div>
              <v-btn-toggle
                v-model="dialogSearchNameMode"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="yiddish">אידיש</v-btn>
                <v-btn flat small value="english">English</v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>

          <!-- Account Search Fields -->
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogLastNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'לעצטע נאמען'
                    : 'Last Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogFirstNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'טאטעס נאמען'
                    : 'Father Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <div class="caption grey--text mb-1">Or Search By Address</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogHouseNumberSearch"
                label="House Number"
                prepend-inner-icon="mdi-home"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogStreetSearch"
                label="Street Name"
                prepend-inner-icon="mdi-road"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Account List -->
          <div class="account-list-container" v-if="dialogAccounts.length > 0">
            <div class="caption grey--text mb-2 px-2">
              Select an account ({{ dialogAccounts.length }} found)
            </div>
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in dialogAccounts"
                :key="account.accountID"
                @click="onAccountSelected(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span class="grey--text ml-2">({{ account.hname }})</span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    Account ID: {{ account.accountID }}
                    <span v-if="account.homePhone" class="ml-1">
                      | {{ account.homePhone }}
                    </span>
                    <span v-if="account.cellPhone" class="ml-1">
                      | {{ account.cellPhone }}
                    </span>
                    <span v-if="account.address1" class="ml-1">
                      | {{ account.address1 }}
                    </span>
                    <span v-if="account.busAddress1" class="ml-1">
                      | Bus: {{ account.busAddress1 }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>

          <!-- No Accounts Message -->
          <v-alert
            v-if="dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            type="warning"
            outline
            dense
            value="true"
            class="mb-3"
          >
            No accounts found. Please try a different search.
          </v-alert>

          <!-- Empty state -->
          <div
            v-if="!dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            class="text-center px-2 pb-3 grey--text"
          >
            <v-icon large color="grey lighten-1">mdi-account-search</v-icon>
            <div class="mt-2">
              Search for an account by phone, name, or address
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

export default {
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    busChangeId: {
      type: Number,
      default: null,
    },
    isViewOnly: {
      type: Boolean,
      default: false,
    },
    sendNotificationOnSave: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    internalDialog: false,
    busChange: null,
    loading: false,
    saving: false,
    busChangeComments: [],
    loadingBusChangeComments: false,

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,

    // Bus routes
    busRoutes: [],
    filteredPickupRoutes: [],
    filteredDropoffRoutes: [],
    selectedPickupRoute: null,
    selectedDropoffRoute: null,
    loadingBusRoutes: false,

    // Bus change notification dialogs and state
    notificationConfirmDialog: false,
    missingNotificationFieldsDialog: false,
    clearedNotificationWarningDialog: false,
    skipNotification: false,
    originalPickupRouteID: null,
    originalDropoffRouteID: null,
    originalNotificationPhone: null,
    originalNotificationType: null,
    savedBusChangeId: null,
    sendingNotification: false,

    // Account search dialog
    accountSearchDialog: false,
    dialogPhoneSearch: "",
    dialogFirstNameSearch: "",
    dialogLastNameSearch: "",
    dialogHouseNumberSearch: "",
    dialogStreetSearch: "",
    dialogAccounts: [],
    dialogSearchNameMode: "yiddish",
    dialogShowNoAccountsMessage: false,
    dialogSearchDebounceTimer: null,

    // Form data
    formData: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      notificationPhoneNumber: "",
      notificationType: "",
      note: "",
    },
    periodOptions: [
      { label: "Morning Pickup", value: "AM" },
      { label: "Afternoon Dropoff", value: "PM" },
    ],
  }),

  computed: {
    dialogTitle() {
      return this.isViewOnly ? "View Bus Change" : "Edit Bus Change";
    },
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
  },

  watch: {
    dialog(val) {
      this.internalDialog = val;
      if (val && this.busChangeId) {
        this.loadBusChange();
      }
    },
    internalDialog(val) {
      if (!val) {
        this.$emit("update:dialog", false);
        this.$emit("close");
      }
    },
    dialogSearchNameMode() {
      // Re-search when switching between English and Yiddish
      if (this.dialogFirstNameSearch || this.dialogLastNameSearch) {
        this.searchDialogByName();
      }
    },
  },

  methods: {
    async loadBusChange() {
      this.loading = true;
      try {
        // Load bus change data
        const busChange = await this.$http.get(
          `api/BusChange/GetById?id=${this.busChangeId}`
        );

        if (!busChange) {
          this.$notify({
            text: "Bus change not found",
            group: "notification",
            type: "error",
          });
          this.close();
          return;
        }

        this.busChange = busChange;
        await this.loadBusChangeComments(
          this.getBusChangeRequestIdFromBusChange(busChange)
        );

        // Set form data
        this.formData = {
          startDate: busChange.startDate
            ? this.formatDateForPicker(busChange.startDate)
            : "",
          endDate: busChange.endDate
            ? this.formatDateForPicker(busChange.endDate)
            : "",
          startPeriod: busChange.startPeriod || busChange.StartPeriod || "",
          endPeriod: busChange.endPeriod || busChange.EndPeriod || "",
          busChangeAddress: busChange.busChangeAddress || "",
          busChangePhoneNumber: busChange.busChangePhoneNumber || "",
          geoPhoneNumber: busChange.geoPhoneNumber || "",
          busChangeName: busChange.busChangeName || "",
          busChangeAccountID: busChange.busChangeAccountID || "",
          notificationPhoneNumber: busChange.notificationPhoneNumber || "",
          notificationType: busChange.notificationType || "",
          note: busChange.note || "",
        };

        // Load bus routes
        await this.loadBusRoutes();

        // Set selected routes
        if (busChange.pickupRouteID) {
          const pickupRoute = this.busRoutes.find(
            (r) => r.id === busChange.pickupRouteID
          );
          this.selectedPickupRoute = pickupRoute || null;
        }
        if (busChange.dropoffRouteID) {
          const dropoffRoute = this.busRoutes.find(
            (r) => r.id === busChange.dropoffRouteID
          );
          this.selectedDropoffRoute = dropoffRoute || null;
        }

        // Track original route IDs for notification detection
        this.originalPickupRouteID = busChange.pickupRouteID || null;
        this.originalDropoffRouteID = busChange.dropoffRouteID || null;

        // Track original notification fields for IVR validation
        this.originalNotificationPhone =
          busChange.notificationPhoneNumber || null;
        this.originalNotificationType = busChange.notificationType || null;
      } catch (e) {
        this.close();
      } finally {
        this.loading = false;
      }
    },

    async loadBusRoutes() {
      this.loadingBusRoutes = true;
      try {
        this.busRoutes = await this.$http.get("api/buschange/GetBusRoutes");

        // Filter routes by building if available
        if (this.busChange && this.busChange.building) {
          const building = this.busChange.building;
          this.filteredPickupRoutes = this.busRoutes.filter((r) => {
            if (r.direction !== "Pickup") return false;
            if (!r.buildings) return true;
            const routeBuildings = r.buildings
              .split(",")
              .map((b) => b.trim().toUpperCase());
            return routeBuildings.includes(building.toString().toUpperCase());
          });

          this.filteredDropoffRoutes = this.busRoutes.filter((r) => {
            if (r.direction !== "Dropoff") return false;
            if (!r.buildings) return true;
            const routeBuildings = r.buildings
              .split(",")
              .map((b) => b.trim().toUpperCase());
            return routeBuildings.includes(building.toString().toUpperCase());
          });
        } else {
          this.filteredPickupRoutes = this.busRoutes.filter(
            (r) => r.direction === "Pickup"
          );
          this.filteredDropoffRoutes = this.busRoutes.filter(
            (r) => r.direction === "Dropoff"
          );
        }
      } catch (e) {
        this.busRoutes = [];
        this.filteredPickupRoutes = [];
        this.filteredDropoffRoutes = [];
      } finally {
        this.loadingBusRoutes = false;
      }
    },

    customRouteFilter(item, queryText, itemText) {
      if (!queryText) return true;
      const search = queryText.toUpperCase();
      const description = (item.description || "").toUpperCase();
      const busNumber = (item.busNumber || "").toString().toUpperCase();
      const driver = (item.driver || "").toUpperCase();
      const color = (item.color || "").toUpperCase();
      const timeStart = (item.timeStart || "").toUpperCase();
      const routeIdentifier = (item.routeIdentifier || "").toUpperCase();

      return (
        description.includes(search) ||
        busNumber.includes(search) ||
        driver.includes(search) ||
        color.includes(search) ||
        timeStart.includes(search) ||
        routeIdentifier.includes(search)
      );
    },

    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860";
    },

    getRouteChipStyle(route) {
      if (!route || !route.color) {
        return {
          background: "#5cb860",
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const bgColor = this.getRouteColor(route.color);
      if (!bgColor) {
        return {
          background: "#5cb860",
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const colorUpper = route.color.toUpperCase().trim();
      const isLightBackground = [
        "BEIGE",
        "WHITE",
        "YELLOW",
        "LIGHT BLUE",
        "LIGHT GREEN",
        "LIGHT PINK",
        "LIGHT PURPLE",
        "SILVER",
        "GOLD",
        "PINK",
        "ORANGE",
        "LAVENDER",
        "PEACH",
        "MINT",
      ].includes(colorUpper);

      if (
        colorUpper === "WHITE" ||
        bgColor.toUpperCase() === "#FFF" ||
        bgColor.toUpperCase() === "#FFFFFF"
      ) {
        return {
          background: "#f5f5f5",
          color: "#000000",
          border: "2px solid #cccccc",
        };
      }

      return {
        background: bgColor,
        color: isLightBackground ? "#000000" : "#FFFFFF",
        border: isLightBackground
          ? "1.5px solid rgba(0, 0, 0, 0.3)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      };
    },

    formatDateForPicker(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatDateTime(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("MM/DD/YYYY hh:mm A");
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "";
      // Remove all non-digit characters
      const cleaned = phoneNumber.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      // Return original if not 10 digits
      return phoneNumber;
    },

    getBusChangeRequestIdFromBusChange(busChange) {
      if (!busChange) {
        return null;
      }

      if (busChange.busChangeRequestID) return busChange.busChangeRequestID;
      if (busChange.busChangeRequestId) return busChange.busChangeRequestId;
      if (busChange.BusChangeRequestID) return busChange.BusChangeRequestID;
      if (busChange.requestID) return busChange.requestID;
      if (busChange.requestId) return busChange.requestId;
      return null;
    },

    getCommentText(comment) {
      if (!comment) return "";
      return comment.commentText || comment.CommentText || "";
    },

    getCommentEnteredBy(comment) {
      if (!comment) return "";
      return comment.enteredBy || comment.EnteredBy || "";
    },

    getCommentEnteredOn(comment) {
      if (!comment) return null;
      return comment.enteredOn || comment.EnteredOn || null;
    },

    getCommentCallerID(comment) {
      if (!comment) return "";
      return comment.callerID || comment.CallerID || "";
    },

    getCommentRecordingUrl(comment) {
      if (!comment) return null;
      return (
        comment.recordingURL ||
        comment.recordingUrl ||
        comment.RecordingURL ||
        null
      );
    },

    getPeriodOrder(period) {
      if (period === "PM") return 2;
      return 1;
    },

    isEndPeriodBeforeStartPeriod(startPeriod, endPeriod) {
      return this.getPeriodOrder(endPeriod) < this.getPeriodOrder(startPeriod);
    },

    async loadBusChangeComments(busChangeRequestId) {
      this.busChangeComments = [];

      if (!busChangeRequestId) {
        return;
      }

      this.loadingBusChangeComments = true;
      try {
        const response = await this.$http.get(
          `api/BusChange/GetBusChangeComments?busChangeRequestId=${busChangeRequestId}`
        );
        this.busChangeComments = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeComments = [];
      } finally {
        this.loadingBusChangeComments = false;
      }
    },

    getTeacherDisplay() {
      if (!this.busChange) return "-";
      const teachers = [];
      if (this.busChange.teacherH) teachers.push(this.busChange.teacherH);
      if (this.busChange.teacherH2) teachers.push(this.busChange.teacherH2);
      return teachers.join(" / ") || "-";
    },

    onPickupRouteChange(pickupRoute) {
      if (!pickupRoute) {
        return;
      }

      // Helper to check if two comma-separated strings have any overlap
      const hasOverlap = (str1, str2) => {
        if (!str1 || !str2) return false;
        const arr1 = str1.split(",").map((s) => s.trim());
        const arr2 = str2.split(",").map((s) => s.trim());
        return arr1.some((item) => arr2.includes(item));
      };

      // Try to find exact match first (highest priority)
      const exactMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          dropoff.busNumber === pickupRoute.busNumber &&
          dropoff.driver === pickupRoute.driver &&
          dropoff.buildings === pickupRoute.buildings &&
          dropoff.grades === pickupRoute.grades &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (exactMatch) {
        this.selectedDropoffRoute = exactMatch;
        return;
      }

      // Try match with overlapping buildings and grades
      const overlapMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          dropoff.busNumber === pickupRoute.busNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.buildings, pickupRoute.buildings) &&
          hasOverlap(dropoff.grades, pickupRoute.grades) &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (overlapMatch) {
        this.selectedDropoffRoute = overlapMatch;
        return;
      }

      // Try looser match - just bus number, driver, and overlapping grades
      const looseMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          dropoff.busNumber === pickupRoute.busNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.grades, pickupRoute.grades)
        );
      });

      if (looseMatch) {
        this.selectedDropoffRoute = looseMatch;
      }
    },

    validateForm() {
      // Check required fields
      if (!this.formData.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.formData.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone numbers if they have values
      const validatePhone = (phoneNumber) => {
        if (!phoneNumber) return true; // Empty is ok (not required)
        const digits = phoneNumber.replace(/[-\s()]/g, "");
        return [10, 11].includes(digits.length) && /^\d+$/.test(digits);
      };

      if (
        this.formData.busChangePhoneNumber &&
        !validatePhone(this.formData.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Bus Change Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.formData.geoPhoneNumber &&
        !validatePhone(this.formData.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.formData.notificationPhoneNumber &&
        !validatePhone(this.formData.notificationPhoneNumber)
      ) {
        this.$notify({
          text:
            "Notification Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate endDate is not before startDate
      if (this.formData.endDate && this.formData.startDate) {
        if (this.formData.endDate < this.formData.startDate) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return false;
        }

        if (
          this.formData.endDate === this.formData.startDate &&
          this.isEndPeriodBeforeStartPeriod(
            this.formData.startPeriod,
            this.formData.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return false;
        }
      }

      return true;
    },

    openAccountSearch() {
      if (this.isViewOnly) return;
      // Reset dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
      this.accountSearchDialog = true;
    },

    closeAccountSearchDialog() {
      this.accountSearchDialog = false;
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    async searchDialogByPhone(value) {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByName() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const lastName = this.dialogLastNameSearch
        ? this.dialogLastNameSearch.trim()
        : "";
      const firstName = this.dialogFirstNameSearch
        ? this.dialogFirstNameSearch.trim()
        : "";

      if (!lastName && !firstName) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .dialogSearchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByAddress() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const houseNumber = this.dialogHouseNumberSearch
        ? this.dialogHouseNumberSearch.trim()
        : "";
      const street = this.dialogStreetSearch
        ? this.dialogStreetSearch.trim()
        : "";

      if (!houseNumber && !street) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.formData.busChangeAccountID = account.accountID;
      // Use name field if available, otherwise combine lastName + firstName
      this.formData.busChangeName =
        account.name ||
        ((account.lastName || "") + " " + (account.firstName || "")).trim();
      if (account.address1 && !this.formData.busChangeAddress) {
        this.formData.busChangeAddress = account.address1;
      }

      // Clear dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    // Notification dialog handlers
    proceedWithNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = true; // Flag to send notification after save
      this.save();
    },

    cancelSave() {
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
    },

    saveWithoutNotification() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.save();
    },

    closeMissingFieldsDialog() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      // User can now fill in the fields and try saving again
    },

    cancelClearedNotificationWarning() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      // User stays in edit mode to add notification fields back
    },

    confirmSaveWithoutNotification() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.save();
    },

    async sendBusChangeNotification(busChangeId) {
      this.sendingNotification = true;
      try {
        const apiUrl = `https://broadcast-3t6ck5zrrq-ue.a.run.app/busChangeNotification/sendBusChangeNotification?busChangeId=${busChangeId}`;
        const response = await this.$http.post(apiUrl, {});

        if (response && !response.error) {
          this.$notify({
            text: "Bus change notification sent successfully",
            group: "notification",
            type: "success",
          });
        } else {
          this.$notify({
            text: `Notification could not be sent: ${response.error ||
              "Unknown error"}`,
            group: "notification",
            type: "warning",
          });
        }
      } catch (error) {
        this.$notify({
          text:
            "Failed to send bus change notification. The bus change was saved but notification failed.",
          group: "notification",
          type: "warning",
        });
      } finally {
        this.sendingNotification = false;
      }
    },

    async save() {
      if (this.isViewOnly) return;

      if (!this.validateForm()) {
        return;
      }

      // Check if notification fields were cleared for IVR sources
      if (!this.skipNotification) {
        const hadNotificationInfo =
          this.originalNotificationPhone || this.originalNotificationType;
        const currentHasNotificationInfo =
          this.formData.notificationPhoneNumber &&
          this.formData.notificationType;

        if (hadNotificationInfo && !currentHasNotificationInfo) {
          this.clearedNotificationWarningDialog = true;
          return;
        }
      }

      // Check if notification should be sent
      if (!this.skipNotification) {
        const currentPickupID = this.selectedPickupRoute
          ? this.selectedPickupRoute.id
          : null;
        const currentDropoffID = this.selectedDropoffRoute
          ? this.selectedDropoffRoute.id
          : null;

        // Check if routes were changed or assigned for the first time
        const routesChanged =
          currentPickupID !== this.originalPickupRouteID ||
          currentDropoffID !== this.originalDropoffRouteID;

        // Both routes must be assigned to send notification
        const bothRoutesAssigned = currentPickupID && currentDropoffID;
        const shouldSendNotification =
          this.sendNotificationOnSave || routesChanged;

        if (shouldSendNotification && bothRoutesAssigned) {
          // Check if notification fields are filled
          const hasNotificationPhone = this.formData.notificationPhoneNumber;
          const hasNotificationType = this.formData.notificationType;

          if (!hasNotificationPhone || !hasNotificationType) {
            // Show missing fields dialog
            this.missingNotificationFieldsDialog = true;
            return;
          } else {
            // Show notification confirmation dialog
            this.notificationConfirmDialog = true;
            return;
          }
        }
      }

      this.saving = true;
      try {
        const data = {
          studentID: this.busChange.studentID,
          startDate: this.formData.startDate
            ? moment(this.formData.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.formData.endDate
            ? moment(this.formData.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.formData.startPeriod,
          endPeriod: this.formData.endPeriod,
          homeAddress: this.busChange.homeAddress || "",
          busChangeAddress: this.formData.busChangeAddress,
          busChangePhoneNumber: this.formData.busChangePhoneNumber,
          geoPhoneNumber: this.formData.geoPhoneNumber,
          busChangeName: this.formData.busChangeName,
          busChangeAccountID: this.formData.busChangeAccountID,
          notificationPhoneNumber: this.formData.notificationPhoneNumber,
          notificationType: this.formData.notificationType,
          pickupRouteID: this.selectedPickupRoute
            ? this.selectedPickupRoute.id
            : null,
          dropoffRouteID: this.selectedDropoffRoute
            ? this.selectedDropoffRoute.id
            : null,
          note: this.formData.note,
          building: this.busChange.building,
          branch: this.busChange.branch,
          class: this.busChange.class,
          section: this.busChange.section,
          term: this.busChange.term,
          enteredBy: this.$store.state.authentication.user,
          source: this.busChange.source || "ADMIN PORTAL",
        };

        // Include ID when editing
        if (this.busChangeId) {
          data.id = this.busChangeId;
        }

        const response = await this.$http.post("api/buschange/save", data);

        if (response.error) {
          this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
          return;
        }

        // Get the bus change ID from response
        const busChangeId = response.id || this.busChangeId;

        this.$notify({
          text: "Bus change updated successfully",
          group: "notification",
          type: "success",
        });

        // Send notification if needed
        if (this.savedBusChangeId && busChangeId) {
          await this.sendBusChangeNotification(busChangeId);
        }

        this.$emit("saved");
        this.close();
      } catch (e) {
        this.$notify({
          text: "Failed to update bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
        this.skipNotification = false;
        this.savedBusChangeId = null;
      }
    },

    close() {
      this.internalDialog = false;
      this.busChange = null;
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.formData = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        notificationPhoneNumber: "",
        notificationType: "",
        note: "",
      };
      this.selectedPickupRoute = null;
      this.selectedDropoffRoute = null;
      this.originalPickupRouteID = null;
      this.originalDropoffRouteID = null;
      this.originalNotificationPhone = null;
      this.originalNotificationType = null;
      this.skipNotification = false;
      this.savedBusChangeId = null;
    },
  },
};
</script>

<style scoped>
.bus-change-dialog .dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.bus-change-dialog .dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: rgb(92, 184, 96);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f5e9;
}

.selected-student-card {
  background-color: #f5f5f5;
  border: 2px solid rgb(92, 184, 96) !important;
}

.selected-student-card .v-card__text {
  padding: 12px !important;
}

.record-info-with-audio {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.record-info-compact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 11px;
  color: #757575;
  padding: 4px 8px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  flex: 1;
  min-width: 300px;
}

.record-info-compact .info-item {
  display: inline-flex;
  align-items: center;
}

.record-info-compact .info-divider {
  margin: 0 8px;
  color: #bdbdbd;
}

.recording-inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
  border-radius: 8px;
  border: 1px solid #ffcccc;
  box-shadow: 0 1px 3px rgba(255, 107, 107, 0.1);
  min-width: 280px;
}

.recording-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.recording-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.recording-label {
  font-size: 11px;
  font-weight: 600;
  color: #d63031;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recording-player {
  width: 100%;
  max-width: 280px;
  height: 32px;
  border-radius: 4px;
}

.comments-card {
  background: #fafafa;
  max-height: 460px;
  overflow-y: auto;
}

.compact-info {
  background: #fafafa;
}

.info-row {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 12px;
  color: #757575;
  min-width: 56px;
  flex-shrink: 0;
}

.info-value {
  font-size: 12px;
  color: #212121;
  font-weight: 500;
}

.comment-entry {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: white;
}

.comment-entry:last-child {
  margin-bottom: 0;
}

.comment-meta {
  font-size: 12px;
  color: #616161;
  margin-bottom: 4px;
}

.comment-caller {
  font-size: 12px;
  margin-bottom: 4px;
}

.comment-text {
  font-size: 13px;
  white-space: pre-wrap;
}

.comment-audio {
  width: 100%;
  min-height: 36px;
}

.period-helper-text {
  line-height: 1.35;
  padding-left: 2px;
}

.period-section-card {
  background: #fcfcfc;
  border-color: #e9e9e9 !important;
}

.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.route-selection-wrapper {
  width: 100%;
}

.route-selection-main {
  display: flex;
  align-items: center;
}

.route-selection-driver {
  font-size: 0.75rem;
  color: #757575;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.route-detail-chip {
  background-color: #e0e0e0;
  color: #000;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 8px;
  display: inline-block;
}

.route-list-item {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: 8px;
  transition: all 0.2s ease;
}

.route-list-item:hover {
  background-color: rgba(92, 184, 96, 0.05);
}

.save-btn {
  min-width: 120px;
}

/* Dialog Header Styles */
.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

/* Account Search Styles */
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.account-list-container {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.account-list {
  background: transparent;
}

.account-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.account-item:hover {
  background-color: #e8f5e9 !important;
}

.account-item:last-child {
  border-bottom: none;
}
</style>
